import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { Category } from '../../types';
import { buildCategoryHierarchy, getCategoryPath } from '../../utils/categoryUtils';
import './CategoryAutocomplete.css';

interface CategoryAutocompleteProps {
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  maxSelections?: number;
  userCategoryIds?: number[];
}

const normalize = (value: string) => value.trim().toLowerCase();

const buildCategoriesWithPath = (categories: Category[]): Category[] =>
  categories.map(category => ({
    ...category,
    displayName: getCategoryPath(category.id, categories)
  }));

const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  selectedIds,
  onChange,
  maxSelections = 3,
  userCategoryIds = []
}) => {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Category[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await buildCategoryHierarchy();
        setAllCategories(data);
      } catch (err) {
        console.error('카테고리 로딩 실패:', err);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const query = normalize(inputValue);
    if (!query) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    const filtered = allCategories.filter(category => {
      const displayName = category.displayName || category.name;
      return displayName.toLowerCase().includes(query) && !selectedIds.includes(category.id);
    });

    setSuggestions(filtered);
    setActiveIndex(-1);
  }, [inputValue, allCategories, selectedIds]);

  const userCategories = useMemo(() => {
    const idSet = new Set(userCategoryIds);
    return allCategories
      .filter(category => idSet.has(category.id) && !selectedIds.includes(category.id))
      .slice(0, 12);
  }, [allCategories, selectedIds, userCategoryIds]);

  const masterCategories = useMemo(
    () =>
      allCategories
        .filter(category => !selectedIds.includes(category.id))
        .slice(0, 12),
    [allCategories, selectedIds]
  );

  const canSelectMore = selectedIds.length < maxSelections;

  const handleSelectCategory = (categoryId: number) => {
    if (selectedIds.includes(categoryId)) return;
    if (!canSelectMore) {
      alert(`최대 ${maxSelections}개까지 선택할 수 있습니다.`);
      return;
    }

    onChange([...selectedIds, categoryId]);
    setInputValue('');
    setIsOpen(false);
  };

  const refreshCategories = async () => {
    const response = await axiosInstance.get('/api/categories');
    const updatedCategories = buildCategoriesWithPath(response.data);
    setAllCategories(updatedCategories);
    return updatedCategories;
  };

  const handleCreateCategory = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!canSelectMore) {
      alert(`최대 ${maxSelections}개까지 선택할 수 있습니다.`);
      return;
    }

    setLoading(true);
    try {
      const parts = trimmed.split('>').map(part => part.trim()).filter(Boolean);
      let parentId: number | null = null;
      let finalCategoryId: number | null = null;
      const workingCategories = [...allCategories];

      for (const partName of parts) {
        const found = workingCategories.find(
          category =>
            normalize(category.name) === normalize(partName) &&
            (category.parent_id || null) === parentId
        );

        if (found) {
          parentId = found.id;
          finalCategoryId = found.id;
          continue;
        }

        const response = await axiosInstance.post('/api/categories', {
          name: partName,
          type: parentId ? 'sub' : 'main',
          parent_id: parentId,
          description: '주제영역 등록 화면에서 생성'
        });

        const newCategory: Category = {
          id: response.data.id,
          name: partName,
          parent_id: parentId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        workingCategories.push(newCategory);
        parentId = newCategory.id;
        finalCategoryId = newCategory.id;
      }

      if (!finalCategoryId) {
        throw new Error('카테고리 생성 실패');
      }

      await refreshCategories();
      onChange([...selectedIds, finalCategoryId]);
      setInputValue('');
      setIsOpen(false);
    } catch (err) {
      console.error('카테고리 생성 실패:', err);
      alert('카테고리 생성 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isOpen && activeIndex >= 0 && suggestions[activeIndex]) {
        handleSelectCategory(suggestions[activeIndex].id);
      } else if (inputValue.trim()) {
        handleCreateCategory();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleRemoveCategory = (categoryId: number) => {
    onChange(selectedIds.filter(id => id !== categoryId));
  };

  const exactMatchExists = allCategories.some(category => {
    const query = normalize(inputValue);
    return query && normalize(category.displayName || category.name) === query;
  });

  const renderQuickButton = (category: Category) => (
    <button
      key={category.id}
      type="button"
      className="category-quick-chip"
      onClick={() => handleSelectCategory(category.id)}
      disabled={!canSelectMore || loading}
      title={category.displayName || category.name}
    >
      {category.displayName || category.name}
    </button>
  );

  return (
    <div className="category-autocomplete-container" ref={dropdownRef}>
      <div className="category-source-grid">
        <section className="category-source-section">
          <div className="category-source-title">내가 사용한 카테고리</div>
          <div className="category-quick-list">
            {userCategories.length > 0 ? (
              userCategories.map(renderQuickButton)
            ) : (
              <span className="category-empty-text">아직 사용 이력이 없습니다.</span>
            )}
          </div>
        </section>

        <section className="category-source-section">
          <div className="category-source-title">사이트 마스터 카테고리</div>
          <div className="category-quick-list">
            {masterCategories.length > 0 ? (
              masterCategories.map(renderQuickButton)
            ) : (
              <span className="category-empty-text">선택 가능한 카테고리가 없습니다.</span>
            )}
          </div>
        </section>
      </div>

      <div className="category-input-wrapper">
        <input
          type="text"
          value={inputValue}
          onChange={event => {
            setInputValue(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={
            canSelectMore
              ? '카테고리 검색 또는 신규 입력 예: 정치 > 선거'
              : `최대 ${maxSelections}개 선택 완료`
          }
          className="category-text-input"
          disabled={!canSelectMore || loading}
        />

        {isOpen && inputValue.trim() && (
          <ul className="category-suggestions-list">
            {suggestions.map((category, index) => (
              <li
                key={category.id}
                onClick={() => handleSelectCategory(category.id)}
                className={`category-suggestion-item ${index === activeIndex ? 'active' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="category-suggestion-label">마스터</span>
                {category.displayName || category.name}
              </li>
            ))}

            {!exactMatchExists && inputValue.trim() && (
              <li onClick={handleCreateCategory} className="category-suggestion-item create-item">
                새 카테고리 생성: "{inputValue.trim()}"
              </li>
            )}
          </ul>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="selected-categories-container">
          {selectedIds.map(id => {
            const category = allCategories.find(item => item.id === id);
            return (
              <span key={id} className="selected-category-chip">
                {category ? category.displayName || category.name : `ID: ${id}`}
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(id)}
                  className="remove-category-btn"
                  aria-label="카테고리 제거"
                >
                  x
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoryAutocomplete;
